import React, { useState } from 'react';
import {
  Box,
  Divider,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import type { EnvironmentDocument, EnvStatusLevel, SetEnvironmentStatusMessageRequest } from '@finvari/deliver';
import { githubPullRequestUrl, reconstructEnvironmentStatus } from '@finvari/deliver';
import { setEnvironmentStatusMessage } from '../firebase';

interface EnvironmentStatusSectionProps {
  environment: EnvironmentDocument;
  // Only editors get the edit affordance; everyone can see a published message.
  canEdit: boolean;
  // PR number of the environment's current rollout, used to build the "View PR" action link so the
  // preview matches the deployed banner. Absent / 0 ⇒ no PR link.
  prNumber?: number | null;
}

const LEVELS: EnvStatusLevel[] = ['info', 'warning', 'error'];

// The status message shown in the deployed app's top bar. Mirrors the test-run "Developer Notes"
// pattern: read-only by default with an edit pencil; editing reveals the level/message form.
// The edit form manages only the MANUAL message (cached on the environment doc's environmentStatus).
// The read view shows the full PUBLISHED message — the manual text combined with the current
// lease — reconstructed the same way functions build globalConfig.json, with its action links.
export default function EnvironmentStatusSection({ environment, canEdit, prNumber }: EnvironmentStatusSectionProps) {
  // The manual message only (drives the edit form pre-fill and the Clear affordance).
  const current = environment.environmentStatus;
  // The reconstructed, published message with its action links (drives the read view).
  const published = reconstructEnvironmentStatus(environment, {
    environmentUrl: `${window.location.origin}/environment/${encodeURIComponent(environment.gcpProjectId)}`,
    pullRequestUrl: githubPullRequestUrl(prNumber),
  });

  const [editing, setEditing] = useState(false);
  const [level, setLevel] = useState<EnvStatusLevel>('info');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState<'save' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Nothing to show: no published message and the viewer can't add one.
  if (!editing && !published && !canEdit) return null;

  const startEdit = () => {
    setLevel(current?.level ?? 'info');
    setMessage(current?.message ?? '');
    setError(null);
    setEditing(true);
  };

  const call = async (req: SetEnvironmentStatusMessageRequest, which: 'save' | 'clear') => {
    setSubmitting(which);
    setError(null);
    try {
      await setEnvironmentStatusMessage(req);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to update status message');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Status Message
        </Typography>
        {canEdit && !editing && (
          <IconButton size="small" aria-label="Edit status message" onClick={startEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {editing ? (
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl size="small" disabled={submitting !== null} sx={{ minWidth: 120 }}>
              <InputLabel>Level</InputLabel>
              <Select value={level} label="Level" onChange={(e) => setLevel(e.target.value as EnvStatusLevel)}>
                {LEVELS.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              autoFocus
              placeholder="e.g., This environment is being reset — expect downtime"
              disabled={submitting !== null}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => call({ gcpProjectId: environment.gcpProjectId, level, message: message.trim() }, 'save')}
              disabled={submitting !== null || !message.trim()}
            >
              {submitting === 'save' ? <CircularProgress size={16} /> : 'Save'}
            </Button>
            <Button size="small" onClick={() => setEditing(false)} disabled={submitting !== null}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            {current && (
              <Button
                size="small"
                color="inherit"
                onClick={() => call({ gcpProjectId: environment.gcpProjectId, clear: true }, 'clear')}
                disabled={submitting !== null}
              >
                {submitting === 'clear' ? <CircularProgress size={16} /> : 'Clear'}
              </Button>
            )}
          </Box>
        </Box>
      ) : published ? (
        <Alert severity={published.level}>
          <Box sx={{ whiteSpace: 'pre-line' }}>{published.message}</Box>
          {published.actions && published.actions.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {published.actions.map((action) => (
                <Link key={action.url} href={action.url} target="_blank" rel="noopener noreferrer">
                  {action.label}
                </Link>
              ))}
            </Box>
          )}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No status message. Click the edit button to show a banner in the deployed app's top bar.
        </Typography>
      )}
    </>
  );
}
